/**
 * Kraken WebSocket V2 API
 * @see https://docs.kraken.com/api/docs/websocket-v2/
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  KrakenWsBookMessage,
  KrakenWsOhlcMessage,
  KrakenWsPong,
  KrakenWsSubscribeRequest,
  KrakenWsSubscribeResponse,
  KrakenWsTickerMessage,
  KrakenWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

/**
 * Kraken WebSocket V2 public endpoint.
 * @see https://docs.kraken.com/api/docs/websocket-v2/
 */
export const KRAKEN_WS_PUBLIC_URL = 'wss://ws.kraken.com/v2';

/**
 * Kraken WebSocket channel descriptors.
 * @see https://docs.kraken.com/api/docs/websocket-v2/
 */
export const KRAKEN_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  ticker: {
    channel: 'ticker',
    topic: 'ticker',
    supportsBatching: true,
    docUrl: 'https://docs.kraken.com/api/docs/websocket-v2/ticker',
  },
  book: {
    channel: 'book',
    topic: 'book',
    supportsBatching: true,
    docUrl: 'https://docs.kraken.com/api/docs/websocket-v2/book',
  },
  trade: {
    channel: 'trade',
    topic: 'trade',
    supportsBatching: true,
    docUrl: 'https://docs.kraken.com/api/docs/websocket-v2/trade',
  },
  ohlc: {
    channel: 'ohlc',
    topic: 'ohlc',
    supportsBatching: true,
    docUrl: 'https://docs.kraken.com/api/docs/websocket-v2/ohlc',
  },
};

/**
 * Kraken WebSocket topology information.
 * CRITICAL: Server sends heartbeat every second automatically.
 * Client should send ping if no message received in 60 seconds.
 * Plain JSON (no compression).
 */
export const KRAKEN_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: KRAKEN_WS_PUBLIC_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 30000, // Send ping every 30 seconds as a safety measure
  note: 'Plain JSON WebSocket V2. Server auto-sends heartbeat every second. Supports batched subscriptions. Orderbook uses incremental updates with CRC32 checksum.',
};

// ========================================
// WebSocket Subscription Options
// ========================================

export type KrakenConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error';

export interface KrakenWsSubscriptionOptions<T> {
  /** Channel name: "ticker", "book", "trade", "ohlc" */
  channel: string;
  /** Symbol in WS format (e.g., "BTC/USD") */
  symbol: string;
  /** Callback for successfully decoded messages */
  onMessage: (data: T) => void;
  /** Callback for errors (connection, subscription failures) */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: KrakenConnectionStatus) => void;
  /** Book depth: 10, 25, 100, 500, 1000 (default 10) */
  depth?: number;
  /** OHLC interval in minutes: 1, 5, 15, 30, 60, 240, 1440, 10080, 21600 */
  interval?: number;
  /** Custom WebSocket configuration */
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

// ========================================
// Generic WebSocket Subscription
// ========================================

/**
 * Create a generic Kraken WebSocket V2 subscription.
 * Handles ping/pong, subscription management, and automatic reconnection.
 *
 * @returns ReconnectingWebSocket instance (call .close() to unsubscribe)
 */
function createKrakenWsSubscription<T>(
  options: KrakenWsSubscriptionOptions<T>,
): ReconnectingWebSocket {
  const {
    channel,
    symbol,
    onMessage,
    onError,
    onStatusChange,
    depth,
    interval,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let pingInterval: ReturnType<typeof setInterval> | undefined;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(KRAKEN_WS_PUBLIC_URL, [], config);

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');

    // Build subscription message
    const subscribeMsg: KrakenWsSubscribeRequest = {
      method: 'subscribe',
      params: {
        channel,
        symbol: [symbol],
      },
    };

    // Add optional parameters based on channel
    if (channel === 'book' && depth) {
      subscribeMsg.params.depth = depth;
    }
    if (channel === 'ohlc' && interval) {
      subscribeMsg.params.interval = interval;
    }

    ws.send(JSON.stringify(subscribeMsg));

    // Start ping interval (send ping every 30 seconds as safety measure)
    // Note: Kraken server sends heartbeat every second, but we ping proactively
    if (pingInterval) {
      clearInterval(pingInterval);
    }
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ method: 'ping' }));
      }
    }, 30000);
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    try {
      const payload = JSON.parse(event.data);

      // Handle heartbeat (ignore silently)
      if (payload.channel === 'heartbeat') {
        return;
      }

      // Handle pong response
      const pongResponse = payload as KrakenWsPong;
      if (pongResponse.method === 'pong') {
        return;
      }

      // Handle subscription response
      const subResponse = payload as KrakenWsSubscribeResponse;
      if (subResponse.method === 'subscribe') {
        if (!subResponse.success) {
          onError?.(new Error(`Kraken subscription error: ${subResponse.error}`));
        }
        return;
      }

      // Handle status message (system status)
      if (payload.channel === 'status') {
        return;
      }

      // Forward data messages to callback
      onMessage(payload as T);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      onStatusChange?.('error');
    }
  });

  ws.addEventListener('close', () => {
    if (pingInterval) {
      clearInterval(pingInterval);
    }
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    const error = new Error(`Kraken WebSocket error: ${event.type}`);
    onError?.(error);
    onStatusChange?.('error');
  });

  return ws;
}

// ========================================
// Ticker Subscription
// ========================================

export interface KrakenTickerSubscriptionOptions {
  /** Symbol in WS format (e.g., "BTC/USD") */
  symbol: string;
  /** Callback for ticker updates */
  onMessage: (ticker: KrakenWsTickerMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: KrakenConnectionStatus) => void;
}

/**
 * Subscribe to Kraken ticker updates via WebSocket V2.
 * Provides real-time ticker statistics including price, volume, and best bid/ask.
 *
 * @see https://docs.kraken.com/api/docs/websocket-v2/ticker
 */
export function subscribeKrakenTicker(
  options: KrakenTickerSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createKrakenWsSubscription<KrakenWsTickerMessage>({
    channel: 'ticker',
    symbol,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Orderbook Depth Subscription
// ========================================

export interface KrakenBookSubscriptionOptions {
  /** Symbol in WS format (e.g., "BTC/USD") */
  symbol: string;
  /** Depth levels: 10, 25, 100, 500, 1000 (default 10) */
  depth?: number;
  /** Callback for orderbook updates */
  onMessage: (book: KrakenWsBookMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: KrakenConnectionStatus) => void;
}

/**
 * Subscribe to Kraken orderbook depth updates via WebSocket V2.
 * IMPORTANT: First message is snapshot (type: "snapshot"), subsequent are updates (type: "update").
 * Updates are incremental - qty=0 means level should be removed.
 *
 * @see https://docs.kraken.com/api/docs/websocket-v2/book
 */
export function subscribeKrakenBook(options: KrakenBookSubscriptionOptions): ReconnectingWebSocket {
  const { symbol, depth = 10, onMessage, onError, onStatusChange } = options;

  return createKrakenWsSubscription<KrakenWsBookMessage>({
    channel: 'book',
    symbol,
    depth,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Trades Subscription
// ========================================

export interface KrakenTradeSubscriptionOptions {
  /** Symbol in WS format (e.g., "BTC/USD") */
  symbol: string;
  /** Callback for trade updates */
  onMessage: (trade: KrakenWsTradeMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: KrakenConnectionStatus) => void;
}

/**
 * Subscribe to Kraken public trade updates via WebSocket V2.
 * Provides real-time trade execution data.
 *
 * @see https://docs.kraken.com/api/docs/websocket-v2/trade
 */
export function subscribeKrakenTrades(
  options: KrakenTradeSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createKrakenWsSubscription<KrakenWsTradeMessage>({
    channel: 'trade',
    symbol,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// OHLC (Klines) Subscription
// ========================================

export interface KrakenOhlcSubscriptionOptions {
  /** Symbol in WS format (e.g., "BTC/USD") */
  symbol: string;
  /** Interval in minutes: 1, 5, 15, 30, 60, 240, 1440, 10080, 21600 */
  interval: number;
  /** Callback for OHLC updates */
  onMessage: (ohlc: KrakenWsOhlcMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: KrakenConnectionStatus) => void;
}

/**
 * Subscribe to Kraken OHLC (candlestick) updates via WebSocket V2.
 * Provides real-time candlestick data.
 * IMPORTANT: Timestamps are in RFC3339 format - parse with Date.parse().
 *
 * @see https://docs.kraken.com/api/docs/websocket-v2/ohlc
 */
export function subscribeKrakenOhlc(options: KrakenOhlcSubscriptionOptions): ReconnectingWebSocket {
  const { symbol, interval, onMessage, onError, onStatusChange } = options;

  return createKrakenWsSubscription<KrakenWsOhlcMessage>({
    channel: 'ohlc',
    symbol,
    interval,
    onMessage,
    onError,
    onStatusChange,
  });
}
